import { useState, useEffect, createContext, useContext, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'
import type { User, Session } from '@supabase/supabase-js'
import { trackSession, trackEventImmediate } from '@/hooks/useTracking'
import { clearAllCache } from '@/lib/cache'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({ user: null, session: null, loading: true, signOut: async () => {} })

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const trackedSession = useRef(false)

  useEffect(() => {
    // IMPORTANT: Set up auth listener BEFORE getSession to avoid race conditions
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      // Track OAuth logins (Google callback)
      if (event === 'SIGNED_IN' && session && !trackedSession.current) {
        trackedSession.current = true
        localStorage.setItem('has_account', '1')
        setTimeout(() => {
          trackSession()
          trackEventImmediate('login', { method: session.user?.app_metadata?.provider || 'unknown' })
        }, 0)
      }

      // On sign out, clear all cached state including profile caches
      if (event === 'SIGNED_OUT') {
        localStorage.removeItem('subscription_active')
        localStorage.removeItem('subscription_checked_at')
        // Clear per-user profile caches
        Object.keys(localStorage)
          .filter(k => k.startsWith('profile_'))
          .forEach(k => localStorage.removeItem(k))
        clearAllCache()
      }

      // On token refresh, update last activity to keep session alive
      if (event === 'TOKEN_REFRESHED' && session) {
        localStorage.setItem('last_activity', Date.now().toString())
      }
    })

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      if (session) {
        localStorage.setItem('last_activity', Date.now().toString())
      }
    }).catch((err) => {
      console.error('Failed to get session:', err)
      setLoading(false)
    })

    // Periodically refresh session to prevent expiry (every 10 minutes)
    const refreshInterval = setInterval(async () => {
      try {
        const { data: { session: refreshed } } = await supabase.auth.refreshSession()
        if (refreshed) {
          localStorage.setItem('last_activity', Date.now().toString())
        }
      } catch {
        // Silent fail — don't break UX on refresh errors
      }
    }, 10 * 60 * 1000)

    return () => {
      subscription.unsubscribe()
      clearInterval(refreshInterval)
    }
  }, [])

  const signOut = async () => {
    localStorage.removeItem('subscription_active')
    clearAllCache()
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
